import { PageTopBar, PageView, Button } from '../../../components'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchInput } from '../../../components/SearchInput'
import { GENERATE_ORDER_LABEL, GENERATE_SHARED_LABEL } from '../../../navigation/constants'
import { scanPackingCode, fetchSipSiblingByProduct, fetchSipTotePending } from '../../../services/pickingOrderServices'
import { stopRecording } from '../../../redux/webcamRecorderSlice'
import useCameraPreview from '../../../hooks/useCameraPreview'
import { isMobile } from 'react-device-detect'
import { CameraBlockedBanner } from './components/CameraBlockedBanner'
import { SipTotePendingList } from './components/SipTotePendingList'
import { getReviewBlockMessage } from './packingReviewBlock'
import { CameraIcon } from '@heroicons/react/outline'

const WAREHOUSES_WITH_SHARED_LABELS = [100, 133] // hardcoded: show shared-label option for these warehouses

// The scan refusals the operator can act on, keyed by the backend's error code. A code
// missing here (or a status other than 404/409/422) takes the unexpected-error path.
const SCAN_ERROR_CODE_KEYS = {
  SCAN_NOT_FOUND: 'generate_shipment.view.tote_or_unit_code_not_found',
  STOCKLESS_NO_OPEN_REPLENISHMENT: 'generate_shipment.view.unit_code_no_open_replenishment',
  STOCKLESS_CODE_AMBIGUOUS: 'generate_shipment.view.unit_code_on_multiple_orders',
  STOCKLESS_PRODUCT_HAS_EXPIRATION: 'generate_shipment.view.unit_code_product_has_expiration',
  STOCKLESS_UNIT_CODE_UNAVAILABLE: 'generate_shipment.view.unit_code_unavailable',
  STOCKLESS_UNIT_CODE_FORMAT: 'generate_shipment.view.unit_code_format',
}
const REFUSAL_STATUSES = [404, 409, 422]
const scanErrorMessageKey = error => {
  if (!REFUSAL_STATUSES.includes(error.response?.status)) return undefined
  return SCAN_ERROR_CODE_KEYS[error.response.data?.code]
}

export const GenerateShipmentView = () => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const { warehouse_id } = useParams()
  const history = useHistory()
  // CUBBO-2182: when the operator hits the SIP dead-end (no more orders for the
  // current SKU, or after sending an order to review), GenerateShipment routes
  // them here with the tote pre-loaded via `?tote=<unique_code>` so they can keep
  // packing the remaining orders in the same tote without re-scanning the tote.
  const preloadedToteCode = new URLSearchParams(useLocation().search).get('tote')
  const inputRefTote = useRef()
  const inputRefProduct = useRef()
  // Each scan takes a number; a response that comes back after a newer scan started is
  // dropped, so a slow lookup can neither navigate nor overwrite the newer scan's message.
  const scanSeqRef = useRef(0)
  const [toteSearchIsLoading, setToteSearchIsLoading] = useState(false)
  const [ToteNotFound, setToteNotFound] = useState(false)
  const [messageToteNotFound, setMessageToteNotFound] = useState(false)
  const [showProductScanInput, setShowProductScanInput] = useState(false)
  const [scannedTotePickingOrderId, setScannedTotePickingOrderId] = useState(null)
  // CUBBO-2182: orders still pending packing in the scanned SIP tote (cross-SKU).
  // null = not resolved yet, [] = tote fully packed, [...] = rows to show.
  const [totePendingOrders, setTotePendingOrders] = useState(null)
  const {
    videoRef: cameraVideoRef,
    isReady: cameraIsReady,
    error: cameraError,
    retry: retryCamera,
    isRetrying: isCameraRetrying,
    isDenied: cameraIsDenied,
  } = useCameraPreview()

  // The 50ms delay outlives the input whenever the view swaps or unmounts
  // first, so the ref can be null by the time the timer runs.
  const focusToteInput = () => {
    const focusTimer = setTimeout(() => {
      inputRefTote.current?.focus()
    }, 50)
    return () => clearTimeout(focusTimer)
  }
  const focusProductInput = () => {
    const focusTimer = setTimeout(() => {
      inputRefProduct.current?.focus()
    }, 50)
    return () => clearTimeout(focusTimer)
  }

  useEffect(focusToteInput, [])
  // A scan still in flight when the screen unmounts must not navigate or set state.
  useEffect(
    () => () => {
      scanSeqRef.current++
    },
    [],
  )
  useEffect(() => {
    if (showProductScanInput) {
      return focusProductInput()
    }
    return focusToteInput()
  }, [showProductScanInput])

  const goToGenerateLabel = orderId => {
    history.push(GENERATE_ORDER_LABEL.replace(':id', orderId).replace(':warehouse_id', warehouse_id))
  }

  const handleSearchChange = async value => {
    dispatch(stopRecording())

    setToteNotFound(false)
    setShowProductScanInput(false)
    setTotePendingOrders(null)
    setToteSearchIsLoading(false)
    // Bumped before the early return: clearing the input must also drop a scan in flight.
    const scanSeq = ++scanSeqRef.current
    const noValido = /\s/
    if (noValido.test(value) || value === '') {
      return
    }
    const isStale = () => scanSeq !== scanSeqRef.current
    setToteSearchIsLoading(true)
    let scan
    try {
      const sortBy = [{ field: 'order_packing_status' }]
      scan = await scanPackingCode(value, warehouse_id, sortBy)
    } catch (error) {
      if (isStale()) return
      // A network error carries no `response`, so it reads as no refusal and alerts.
      const messageKey = scanErrorMessageKey(error)
      setToteSearchIsLoading(false)
      if (messageKey) {
        setToteNotFound(true)
        setMessageToteNotFound(i18n.t(messageKey))
        return
      }
      console.log(error)
      window.alert(i18n.t('generate_shipment.view.unexpected_error'))
      return
    }
    if (isStale()) return
    if (scan?.kind === 'stockless_order' && scan.order_id) {
      setToteSearchIsLoading(false)
      goToGenerateLabel(scan.order_id)
      return
    }
    const pickingOrder = scan?.kind === 'tote' ? scan.picking_order : null
    if (!pickingOrder?.order) {
      setToteSearchIsLoading(false)
      window.alert(i18n.t('generate_shipment.view.unexpected_error'))
      return
    }
    const order = pickingOrder.order
    const reviewBlock = getReviewBlockMessage(pickingOrder)
    if (reviewBlock) {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t(reviewBlock.key, reviewBlock.params))
      setToteSearchIsLoading(false)
      return
    }
    if (order.status !== 'packing') {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t('generate_shipment.view.order_not_packing'))
      setToteSearchIsLoading(false)
      return
    }

    if (order.channel_integration && order.channel_integration.channel_type_name === 'natura' && !order.has_approved_sale_invoices) {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t('generate_shipment.view.order_invoice_not_ready'))
      setToteSearchIsLoading(false)
      return
    }

    if (order.channel_integration && order.channel_integration.channel_type_name === 'natura' && !order.has_notfis) {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t('generate_shipment.view.order_notfis_not_ready'))
      setToteSearchIsLoading(false)
      return
    }

    if (order.channel_integration && order.is_boleto_required && !order.has_boleto) {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t('generate_shipment.view.order_boleto_not_ready'))
      setToteSearchIsLoading(false)
      return
    }

    setToteSearchIsLoading(false)
    if (pickingOrder.single_item_picking) {
      setScannedTotePickingOrderId(pickingOrder.id)
      setShowProductScanInput(true)
      // CUBBO-2182: surface the other orders still pending in this tote so the
      // operator can scan the next physical product instead of dead-ending.
      // A failure here must not block scanning — degrade to no list.
      try {
        const pending = await fetchSipTotePending(pickingOrder.id)
        if (!isStale()) setTotePendingOrders(pending?.picking_orders ?? [])
      } catch (error) {
        if (!isStale()) setTotePendingOrders(null)
      }
    } else {
      goToGenerateLabel(order.id)
    }
  }

  // Auto-load the pre-loaded tote on mount (CUBBO-2182). Runs the same path as a
  // manual tote scan: for a SIP tote it surfaces the product-scan input so the
  // operator just scans the next physical product. Verification stays intact.
  useEffect(() => {
    if (preloadedToteCode) {
      handleSearchChange(preloadedToteCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleProductSearchChange = async productSkuUpc => {
    if (productSkuUpc === ' ' || productSkuUpc === '') {
      return
    }
    setToteSearchIsLoading(true)
    setToteNotFound(false)
    let pickingOrder = null
    try {
      pickingOrder = await fetchSipSiblingByProduct(scannedTotePickingOrderId, productSkuUpc)
      const reviewBlock = getReviewBlockMessage(pickingOrder)
      if (reviewBlock) {
        setToteNotFound(true)
        setMessageToteNotFound(i18n.t(reviewBlock.key, reviewBlock.params))
        setToteSearchIsLoading(false)
        // The operator stays here to scan the next product; a controlled input
        // still holding the blocked code would concatenate the next scan.
        inputRefProduct.current?.resetInput()
        return
      }
      goToGenerateLabel(pickingOrder.order.id)
    } catch (error) {
      setToteNotFound(true)
      setMessageToteNotFound(i18n.t('generate_shipment.view.order_not_found'))
    }
    setToteSearchIsLoading(false)
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">
            <span className="text-gray-400">{i18n.t('generate_shipment.view.generate_label_title')}</span>{' '}
          </div>
          {WAREHOUSES_WITH_SHARED_LABELS.includes(Number(warehouse_id)) && (
            <Button
              className="whitespace-nowrap mr-2"
              onClick={() => {
                history.push(GENERATE_SHARED_LABEL.replace(':warehouse_id', warehouse_id))
              }}
            >
              {i18n.t('generate_shipment.view.generate_label_button')}
            </Button>
          )}
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <div className="flex-1 flex items-stretch overflow-hidden">
        <main className="flex-1 bg-white p-7 px-80">
          <h2 className="text-lg leading-6 font-medium text-gray-900" style={{ textAlign: 'center' }}>
            {i18n.t('generate_shipment.view.scan_tote_title')}
          </h2>
          <label>{i18n.t('generate_shipment.view.scan_tote_label')}</label>
          {/* CUBBO-2182: prefill with the ?tote= code so the operator can SEE which
                        tote they're currently packing (it's not obvious from the URL). */}
          <SearchInput
            className="sm:col-span-3"
            ref={inputRefTote}
            inputValue={preloadedToteCode || ''}
            onChange={handleSearchChange}
            debounce={500}
            placeholder={i18n.t('generate_shipment.view.scan_tote_placeholder')}
          ></SearchInput>
          {showProductScanInput && (
            <div className="mt-3">
              <div className="font-bold text-center">{i18n.t('generate_shipment.view.multiple_orders')}</div>
              <SipTotePendingList orders={totePendingOrders} />
              <label>{i18n.t('generate_shipment.view.scan_product_label')}</label>
              <SearchInput
                className="sm:col-span-3"
                ref={inputRefProduct}
                onChange={handleProductSearchChange}
                debounce={500}
                placeholder={i18n.t('generate_shipment.view.scan_product_placeholder')}
              />
            </div>
          )}
          {toteSearchIsLoading && <div className="block text-sm font-medium text-gray-700 mt-2">Buscando...</div>}
          {ToteNotFound && !toteSearchIsLoading && <p className="text-red-600">{messageToteNotFound}</p>}
          {!isMobile && (
            <div className="mt-6">
              {cameraError ? (
                <CameraBlockedBanner onRetry={retryCamera} isRetrying={isCameraRetrying} isDenied={cameraIsDenied} />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-md bg-secondary-orange-50 flex-shrink-0 flex items-center justify-center">
                      <CameraIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-base leading-snug my-0">{i18n.t('generate_shipment.view.camera_preview_card_title')}</p>
                      <span className="text-xs text-gray-900 mt-1">{i18n.t('generate_shipment.view.camera_preview_card_subtitle')}</span>
                    </div>
                  </div>
                  <div className="relative w-full rounded-lg overflow-hidden bg-gray-900" style={{ aspectRatio: '16/9' }}>
                    <video ref={cameraVideoRef} autoPlay muted playsInline className={`w-full h-full object-cover${cameraIsReady ? '' : ' hidden'}`} />
                    {!cameraIsReady && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </PageView>
  )
}
